import Collapsible from "@/src/mui-custom/collapse/collapsible/collapsible";
import MathWrapper from "@/src/components/journal-site/articles/article-body/equation/equation";
import Image from "next/image";
import ReactHtmlParser from "react-html-parser";
import TableContent from "@/src/components/journal-site/articles/article-body/table/table-content";
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    CardActions,
    Link,
    Typography
} from "@mui/material";
import type ArticleBodySectionContent from "@/src/_types/Article/article-body-section-content";
import NextLink from "next/link";

const RecursiveContent = (
    {
        content,
    }: {
        content: ArticleBodySectionContent | ArticleBodySectionContent[] | string
    }
) => {
    if (Array.isArray(content)) {
        return (
            <Box>
                {
                    content.map((item, index) => (
                        <RecursiveContent
                            key={index}
                            content={item}
                        />
                    ))
                }
            </Box>
        );
    }
    if (
        typeof content === 'object'
        && content !== null
        && content.type
        && content.type !== ''
    ) {
        switch (content.type) {
            case 'paragraph': {
                return <Typography mb={1} sx={{ textIndent: '1.5rem' }}>
                    {
                        ReactHtmlParser(content.content as string)
                    }
                </Typography>;
            }
            case 'table': {
                return (
                    <Box component="article" mb={2}>
                        <Card
                            id={content.id}
                            sx={{
                                boxShadow: 'none',
                                borderWidth: '1px',
                                borderStyle: 'solid',
                                borderColor: 'custom.lightGray'
                            }}
                        >
                            <CardHeader
                                sx={{
                                    backgroundColor: 'custom.ultraLightGray',
                                    borderBottomWidth: '1px',
                                    borderBottomStyle: 'solid',
                                    borderBottomColor: 'custom.lightGray',
                                    pt: .5,
                                    pb: .75
                                }}
                                title={
                                    <Box lineHeight={.25}>
                                        <Typography
                                            component="strong"
                                            fontWeight="bold"
                                            fontSize={'1rem'}
                                            mr={.25}
                                        >
                                            {
                                                content.label
                                            }
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            fontSize={'.8rem'}
                                            component="span"
                                            color="primary.main"
                                        >
                                            {
                                                ReactHtmlParser(
                                                    content?.caption?.title || ''
                                                )
                                            }
                                        </Typography>
                                    </Box>
                                }
                            />
                            <CardContent
                                sx={{
                                    p: 0,
                                    paddingBottom: '0 !important'
                                }}
                            >
                                <TableContent
                                    content={content.content as string}
                                />
                            </CardContent>
                            <CardActions
                                sx={{
                                    bgcolor: 'custom.ultraLightGray',
                                    display: 'block',
                                    padding: '1rem'
                                }}
                            >
                                <Typography
                                    fontSize={'.8rem'}
                                    ml={0}
                                >
                                    {
                                        ReactHtmlParser(
                                            content?.caption?.p ||
                                            content?.caption?.title ||
                                            ''
                                        )
                                    }
                                </Typography>
                            </CardActions>
                        </Card>
                    </Box>
                );
            }
            case 'figure': {
                const imageUrl = content.url || null;

                return (
                    (imageUrl && imageUrl !== '') &&
                    <Box
                        component="article"
                        id={content.id}
                        mb={2}
                    >
                        <Card
                            id={content.id}
                            sx={{
                                boxShadow: 'none',
                                border: '1px',
                                borderStyle: 'solid',
                                borderColor: 'custom.lightGray'
                            }}
                        >
                            <CardContent sx={{ p: 0 }}>
                                <Link
                                    component={NextLink}
                                    href={content?.url ? `${process.env.NEXT_PUBLIC_SERVICE_URI_CDN_SERVE}${content?.url}` : ''}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ display: 'flex' }}
                                >
                                    <Image
                                        src={`${process.env.NEXT_PUBLIC_SERVICE_URI_CDN_SERVE}${imageUrl}`}
                                        title={content?.caption?.title || ''}
                                        alt={
                                            content?.caption?.p ||
                                            content?.caption?.title ||
                                            ''
                                        }
                                        width={500}
                                        height={500}
                                        style={{
                                            width: '100%',
                                            height: 'auto'
                                        }}
                                    />
                                </Link>
                            </CardContent>
                            <CardActions
                                sx={{
                                    bgcolor: 'custom.lightGray',
                                    display: 'block',
                                    padding: '1rem'
                                }}
                            >
                                <Collapsible
                                    isOpen={true}
                                    label={content?.label || ''}
                                >
                                    <Typography
                                        fontSize={'.8rem'}
                                        ml={0}
                                    >
                                        {
                                            ReactHtmlParser(
                                                content?.caption?.p ||
                                                content?.caption?.title ||
                                                ''
                                            )
                                        }
                                    </Typography>
                                </Collapsible>
                            </CardActions>
                        </Card>
                    </Box>
                );
            }
            case 'equation': {
                return content.url ? (
                    <Box
                        component="article"
                        id={content.id}
                        mb={2}
                        textAlign="left"
                    >
                        <Image
                            src={`${process.env.NEXT_PUBLIC_SERVICE_URI_CDN_SERVE}${content.url}`}
                            title={content?.caption?.title || ''}
                            alt={content?.caption?.title || ''}
                            width={500}
                            height={500}
                            style={{ width: '100%', height: 'auto' }}
                        />
                        <Box>{content.label}</Box>
                    </Box>
                ) : (
                    (content.content && content.content.length > 0) &&
                    (
                        <Box component="article" id={content.id} mb={2} textAlign="left">
                            <MathWrapper>
                                <div dangerouslySetInnerHTML={{ __html: content.content as string }} />
                            </MathWrapper>
                        </Box>
                    )
                );
            }
            case 'section': {
                return (
                    <Box component="article" id={content.id} mb={2}>
                        <Typography
                            variant="h4"
                            fontSize={'1.5rem'}
                            sx={{ marginBlockEnd: '0 !important' }}
                        >
                            {
                                content.title
                            }
                        </Typography>
                        {
                            content.content &&
                            typeof content.content !== 'string' &&
                            content.content.map((child: any, index: number) => (
                                <RecursiveContent
                                    key={index}
                                    content={child}
                                />
                            ))
                        }
                    </Box>
                );
            }
            case 'list': {
                const ListComponent = content.list_type === 'order' ? 'ol' : 'ul';
                return (
                    <ListComponent>
                        {
                            content.content &&
                            typeof content.content !== 'string' &&
                            content.content.map((child: any, index: number) => (
                                <li key={index}>
                                    <RecursiveContent
                                        content={child}
                                    />
                                </li>
                            ))
                        }
                    </ListComponent>
                );
            }
            default: {
                return <Typography>
                    {
                        JSON.stringify(content)
                    }
                </Typography>;
            }
        }
    }

    return <Typography>
        {
            ReactHtmlParser(content as string)
        }
    </Typography>;
};

export default RecursiveContent;